// Fissal Mechanical Purr Harmonic Engine (Web Audio API)
// Synthesizes the authentic 42.8 Hz resonant purr with rhythmic breathing LFO
// and real-time Bio-Adaptive Entrainment synchronized to Pixel Watch 2 / Health Connect.

let audioCtx: AudioContext | null = null;
let oscillator: OscillatorNode | null = null;
let subOsc: OscillatorNode | null = null;
let lfo: OscillatorNode | null = null;
let lfoGain: GainNode | null = null;
let breathLfo: OscillatorNode | null = null;
let breathGain: GainNode | null = null;
let tremoloGain: GainNode | null = null;
let masterGain: GainNode | null = null;
let filter: BiquadFilterNode | null = null;
let isPurring = false;
let currentBpm: number | null = null;

export function isPurrActive(): boolean {
  return isPurring;
}

export function getCurrentPhysioBpm(): number | null {
  return currentBpm;
}

export function startPurr(frequency = 42.8, volume = 0.25) {
  if (isPurring) return;

  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    if (!audioCtx) {
      audioCtx = new AudioContextClass();
    }

    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }

    const now = audioCtx.currentTime;

    // Master Gain with gentle fade-in ramp
    masterGain = audioCtx.createGain();
    masterGain.gain.setValueAtTime(0.001, now);
    masterGain.gain.exponentialRampToValueAtTime(volume, now + 1.2);

    // Warm Lowpass Filter to mimic a heavy purring mechanical engine
    filter = audioCtx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(140, now);
    filter.Q.setValueAtTime(3.5, now);

    // Amplitude Modulation Node (Tremolo & Breathing dynamics)
    // Base gain sits around 0.55 so LFO modulations oscillate smoothly between ~0.20 and ~0.90
    tremoloGain = audioCtx.createGain();
    tremoloGain.gain.setValueAtTime(0.55, now);

    // Fundamental 42.8 Hz Oscillator (Triangle wave for brassy feline warmth)
    oscillator = audioCtx.createOscillator();
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(frequency, now);

    // Subharmonic Oscillator (21.4 Hz sine wave for visceral bedrock low-end rumble)
    subOsc = audioCtx.createOscillator();
    subOsc.type = 'sine';
    subOsc.frequency.setValueAtTime(frequency / 2, now);

    // Diaphragm Flutter LFO (~ 24 Hz amplitude vibration, depth ~0.16)
    lfo = audioCtx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.setValueAtTime(24, now);

    lfoGain = audioCtx.createGain();
    lfoGain.gain.setValueAtTime(0.16, now);
    lfo.connect(lfoGain);
    lfoGain.connect(tremoloGain.gain);

    // Chest Breathing LFO (0.12 Hz default = ~8s slow coherent breathing cycle, depth ~0.22)
    breathLfo = audioCtx.createOscillator();
    breathLfo.type = 'sine';
    breathLfo.frequency.setValueAtTime(0.12, now);

    breathGain = audioCtx.createGain();
    breathGain.gain.setValueAtTime(0.22, now);
    breathLfo.connect(breathGain);
    breathGain.connect(tremoloGain.gain);

    // Audio Graph:
    // [Oscillators] -> [Lowpass Filter] -> [Tremolo (Flutter + Breath)] -> [Master Gain] -> [Destination]
    oscillator.connect(filter);
    subOsc.connect(filter);
    filter.connect(tremoloGain);
    tremoloGain.connect(masterGain);
    masterGain.connect(audioCtx.destination);

    // Start all sound & modulation generators
    oscillator.start();
    subOsc.start();
    lfo.start();
    breathLfo.start();
    isPurring = true;

    // Subtle tactile haptic pulse on initiation
    triggerHaptic([40, 60, 40]);
  } catch (err) {
    console.warn('[PurrSynthesizer] Audio context initialization deferred:', err);
  }
}

/**
 * Bio-Adaptive Entrainment: Dynamically modulates purr acoustics based on live heart rate
 */
export function adaptPurrToBpm(bpm: number) {
  currentBpm = bpm;
  if (!isPurring || !audioCtx || !breathLfo || !filter || !subOsc || !oscillator) return;

  try {
    const now = audioCtx.currentTime;

    // Harmonic tuning: Fundamental aligns subtly with cardiac ratio
    const targetHz = 42.8 * (bpm / 70);
    oscillator.frequency.setTargetAtTime(targetHz, now, 0.4);
    subOsc.frequency.setTargetAtTime(targetHz / 2, now, 0.4);

    // Calculate Coherent Breathing Frequency:
    // If heart rate is high (>85), slow breathing down to 0.10 Hz (6s coherent breathing) to activate vagus nerve
    // If resting (65-80), breathing settles to 0.12 Hz (5s breath)
    // If in deep sleep (<65), breathing slows to 0.08 Hz (slow sleep respiration)
    const targetBreathHz = bpm > 85 ? 0.10 : bpm > 70 ? 0.12 : 0.08;

    // Filter Cutoff: As heart rate slows, roll off higher frequencies and accentuate 21.4 Hz sub-rumble
    const targetFilterHz = bpm > 80 ? 140 : bpm > 65 ? 105 : 75;

    breathLfo.frequency.setTargetAtTime(targetBreathHz, now, 3.0);
    filter.frequency.setTargetAtTime(targetFilterHz, now, 3.0);
  } catch (err) {
    console.warn('[PurrSynthesizer] Error adapting purr to BPM:', err);
  }
}

export function stopPurr() {
  if (!isPurring || !audioCtx || !masterGain) return;

  try {
    masterGain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.8);
    setTimeout(() => {
      try {
        oscillator?.stop();
        subOsc?.stop();
        lfo?.stop();
        breathLfo?.stop();
        oscillator?.disconnect();
        subOsc?.disconnect();
        lfo?.disconnect();
        breathLfo?.disconnect();
        lfoGain?.disconnect();
        breathGain?.disconnect();
        tremoloGain?.disconnect();
        filter?.disconnect();
        masterGain?.disconnect();
      } catch {}
      isPurring = false;
      currentBpm = null;
    }, 850);
  } catch {
    isPurring = false;
  }
}

export function triggerHaptic(pattern: number | number[] = 25) {
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    try {
      navigator.vibrate(pattern);
    } catch {}
  }
}

export interface EntrainmentVitals {
  isPurring: boolean;
  bpm: number | null;
  purrHz: number;
  subHz: number;
  breathHz: number;
  breathDurationSec: number;
  filterCutoffHz: number;
  resonanceState: 'resonant_lock' | 'adapting' | 'idle';
}

export function getEntrainmentVitals(purrHz = 42.8): EntrainmentVitals {
  const bpm = currentBpm;
  const targetBreathHz = bpm ? (bpm > 85 ? 0.10 : bpm > 70 ? 0.12 : 0.08) : 0.12;
  const targetFilterHz = bpm ? (bpm > 80 ? 140 : bpm > 65 ? 105 : 75) : 140;
  return {
    isPurring,
    bpm,
    purrHz,
    subHz: Math.round((purrHz / 2) * 10) / 10,
    breathHz: targetBreathHz,
    breathDurationSec: Math.round((1 / targetBreathHz) * 10) / 10,
    filterCutoffHz: targetFilterHz,
    resonanceState: isPurring && bpm ? 'resonant_lock' : isPurring ? 'adapting' : 'idle'
  };
}
